import { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Typography } from '../../../../core/components/molecules/Typography';
import { useGetPracticesDetail } from '../../hooks/getPracticeDetail';
import { useSetAnswer } from '../../hooks/answer';
import { useFinishPractice } from '../../hooks/finishPractice';
import { IExercise } from '../../../../core/models/exercise';
import { useExamExerciseListHook } from '../../../../core/hooks/examExerciseList';
import { normalizeExercises } from '../../utils/data';
import { formatTimeForPractice } from '../../utils/time';
import { useSendPracticeEssay } from '../../hooks/sendPracticeEssay';
import { practiceEssayStore } from '../../stores/practiceEssayStore';
import { IEssayBody } from '../../../../core/models/essay';
import { Breadcrumbs } from '../../../../core/components/organism/Breadcrumbs';
import { AnswerSheet } from '../../../../core/components/organism/AnswerSheet';
import { IntersectionIcon } from 'morpheus-ui';
import { ExamExerciseList } from '../../../../core/components/organism/Exam/ExerciseList';
import { toast } from '../../../../core/components/atoms/Toast';
import { usePrintComponentStore } from '../../../../core/components/organism/PrintComponent/store/usePrintComponentStore';
import { useEssayCoreStore } from '../../../../core/stores/essayCoreStore';
import NewPrintComponent from '../../../../core/components/organism/PrintComponent/newPrintComponent';
import ExercisePaper from '../../../../core/components/templates/PrintPaper/exercises';
import ExamNavigation from '../../../../core/components/molecules/Exam/Navigation';
import Container from '../../../../core/components/atoms/Container';
import ConfirmationModal from '../../components/ActionModal';
import PracticeEssayTheme from './components/essayTheme';
import ContentLoader from 'react-content-loader';
import NoContent from '../../../../core/components/templates/NoContent';

export function PracticeRunning() {
  const navigate = useNavigate();
  const { simuladoId } = useParams();
  const {
    data,
    isLoading,
    isError: getError,
  } = useGetPracticesDetail(simuladoId || '');

  const { essay } = practiceEssayStore();

  const { mutate: sendEssay, isPending: pendingEssay } = useSendPracticeEssay();
  const { mutate: mutateFinish, isPending: pendingFinish } =
    useFinishPractice();

  const { setMultipleExerciseAlternatives } = useExamExerciseListHook();
  const { mutate } = useSetAnswer();
  const { setData } = usePrintComponentStore();
  const { text, image, title } = useEssayCoreStore();

  const [choices, setChoices] = useState<IExercise[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isOpenAnswerSheet, setIsOpenAnswerSheet] = useState<boolean>(false);
  const [isOpenPrint, setIsOpenPrint] = useState<boolean>(false);
  const [selectId, setSelectId] = useState<string>();
  const [isByTime, setIsByTime] = useState<boolean>(false);
  const [isTextSent, setIsTextSent] = useState<boolean>(false);

  useEffect(() => {
    if (data) {
      const normalized = normalizeExercises(data?.exercises);
      const history = data.history.answers.map((item) => ({
        question: item.question,
        answer: item.answer,
      }));

      const updatedExercises = normalized.map((it) => {
        const exercise = history.find((answer) => answer.question === it.id);

        if (exercise && exercise.answer) {
          // Função para verificar se a string pode ser convertida para JSON
          const isJson = (str: string) => {
            try {
              JSON.parse(str);
              return true;
            } catch (e) {
              return false;
            }
          };

          let answerIds: string[] = [];

          // Processar a resposta baseado no formato
          if (isJson(exercise.answer)) {
            // Se for JSON, fazer o parse
            const parsed = JSON.parse(exercise.answer);
            answerIds = Array.isArray(parsed) ? parsed : [parsed];
          } else if (exercise.answer.includes(',')) {
            // Se tiver vírgula, fazer split
            answerIds = exercise.answer.split(',').map((id) => id.trim());
          } else {
            // Se for ID único
            answerIds = [exercise.answer];
          }

          // Atualizar o exercício com as alternativas selecionadas
          return setMultipleExerciseAlternatives(answerIds, it as IExercise);
        }

        return it;
      });

      setChoices(updatedExercises);
    }
  }, [data]);

  useEffect(() => {
    if (selectId) {
      const element = document.getElementById(selectId);

      if (element) {
        element?.scrollIntoView(true);
      }

      setSelectId(undefined);
    }
  }, [selectId]);

  const onSimulationFinish = () => {
    if (simuladoId) {
      mutateFinish(simuladoId, {
        onSuccess: () => {
          navigate('/simulados/feedback/' + simuladoId);
        },
        onError: () => {
          toast({
            variant: 'error',
            message: 'Não foi possivel finalizar o simulado',
            title: 'Erro!',
            position: 'top-right',
          });
        },
      });
    }
  };

  const onSaveEssay = () => {
    if (essay) {
      if (
        !image &&
        (!text || text.length === 0 || text === '' || text === null)
      ) {
        toast({
          variant: 'error',
          message: 'O seu texto está em branco',
          title: 'Erro!',
          position: 'top-right',
        });

        return;
      }

      const body: IEssayBody = {
        image: text ? null : image,
        text: image ? null : text,
        theme: essay.theme.id || essay.theme._id || '',
        tryNumber: essay.tryNumber || 0,
        fontSize: essay.fontSize,
        title: title || '',
      };

      sendEssay(body, {
        onSuccess: () => onSimulationFinish(),
        onError: () => {
          toast({
            variant: 'error',
            message: 'Não foi possivel enviar a redação',
            title: 'Erro!',
            position: 'top-right',
          });
        },
      });
    }
  };

  const finish = () => {
    if (data?.hasEssay && !isTextSent) {
      onSaveEssay();
    } else {
      onSimulationFinish();
    }
  };

  const onClose = () => {
    setIsOpen(false);
    setIsByTime(false);
  };

  const handlePrint = () => {
    setIsOpenPrint(true);
  };

  const onAnswer = (exercise: IExercise, action?: string | null) => {
    if (action === 'selectAlternative') {
      const aux = [...choices];
      const idx = aux.findIndex(
        (it) => it._id === exercise._id || it.id === exercise.id
      );

      if (idx >= 0) {
        aux[idx] = exercise;
      }

      // Para múltipla escolha, coletar todas as alternativas selecionadas
      if (
        exercise.exerciseType === 'multiple_answer' ||
        exercise.exercise_type === 'multiple_answer'
      ) {
        const selectedAlternatives = exercise.alternatives.filter(
          (alternative) => alternative.selected === true
        );

        // Coletar todos os IDs selecionados
        const answerIds = selectedAlternatives.map((alt) => alt.id || alt._id);

        const question = exercise._id || exercise.id;

        // Enviar array de IDs para múltipla escolha
        setChoices(aux);
        mutate({
          answer: answerIds.length > 0 ? JSON.stringify(answerIds) : '',
          question,
          simulated: simuladoId || '',
        });
      } else {
        // Para escolha única, manter lógica original
        const answerItem = exercise.alternatives.find(
          (alternative) => alternative.selected === true
        );
        const question = exercise._id || exercise.id;
        const answer = answerItem?.id || answerItem?._id || '';

        setChoices(aux);
        mutate({ answer: answer, question, simulated: simuladoId || '' });
      }
    }
  };

  const hasPendences = () => {
    if (choices && data) {
      const answered = choices.filter((it) => it.hasAlternativeSelected);

      if (answered.length === data.exercises.length) {
        return false;
      }

      return true;
    }
  };

  const onAutoFinish = (isTimeExpired?: boolean) => {
    setIsOpen(true);

    if (isTimeExpired) {
      setIsByTime(true);
    }
  };

  const showContent = () => {
    return (
      <div className='mb-10 flex flex-col gap-xl'>
        <ExamExerciseList
          isLoading={isLoading}
          data={choices}
          onExerciseChange={(exercise, action) => onAnswer(exercise, action)}
          showExerciseArea={true}
          selectedId={selectId}
          orderBy='area'
          clearSelected={() => {
            setSelectId(undefined);
            setIsOpenAnswerSheet(false);
          }}
        />

        {data?.hasEssay && (
          <PracticeEssayTheme
            theme={data.essayTheme}
            setIsTextSent={setIsTextSent}
          />
        )}

        <ExamNavigation
          exercisesDone={
            choices?.filter((it) => it.hasAlternativeSelected)?.length || 0
          }
          totalExercises={data?.exercises.length || 0}
          timeLeft={formatTimeForPractice(data?.time, data?.startDate)}
          isLoading={isLoading}
          isPractice={true}
          onFinish={(isTimeExpired?: boolean) => onAutoFinish(isTimeExpired)}
          onPrint={() => {
            setData(choices);
            handlePrint();
          }}
          onAnswerCardClick={() => setIsOpenAnswerSheet(true)}
        />

        <NewPrintComponent
          type='practice'
          isOpen={isOpenPrint}
          onClose={() => setIsOpenPrint(false)}
          title='Imprimir simulado'
          subtitle='Exércicios'
        >
          <ExercisePaper
            cols='split'
            size='default'
            color='interdisciplinar'
            iconType='svg'
            icon={IntersectionIcon}
            title='Exercícios'
            module='interdisciplinar'
          />
        </NewPrintComponent>
      </div>
    );
  };

  if (getError) {
    const url = `/simulados/provas`;

    return (
      <div className='min-h-screen w-full bg-color-lighter md:mb-0'>
        <NoContent url={url} />
      </div>
    );
  }

  return (
    <div className='min-h-screen w-full bg-color-lighter md:mb-0'>
      <Container
        gap='xxl'
        direction='col'
        className='xs:mb-[30px] xl:mb-[30px]'
      >
        <Breadcrumbs
          items={[
            {
              label: 'Simulador de provas',
              href: `/simulados/provas`,
            },
            {
              label: 'Criar simulado',
              href: `/simulados/criar`,
            },
            {
              label: data?.title || 'Simulado',
              href: '#',
            },
          ]}
        />
        {isLoading ? (
          <Fragment>
            {Array.from({ length: 10 }, (_, index) => (
              <ContentLoader
                key={index}
                backgroundColor={'#a7adba'}
                foregroundColor={'#c0c5ce'}
                viewBox='0 0 500 40'
              >
                <rect x='0' y='2' rx='4' ry='2' width='300' height='20' />
                <rect x='0' y='25' rx='2' ry='2' width='250' height='10' />
              </ContentLoader>
            ))}
          </Fragment>
        ) : (
          showContent()
        )}

        <AnswerSheet
          essay={
            data?.essay && data.essay.id
              ? { essay: data?.essay, origin: 'running' }
              : undefined
          }
          isOpen={isOpenAnswerSheet}
          answers={choices}
          buttonText='Fechar'
          orderBy='area'
          onClose={() => setIsOpenAnswerSheet(false)}
          onClick={() => setIsOpenAnswerSheet(false)}
          onSelect={(id: string) => setSelectId(id)}
        />

        <ConfirmationModal
          text='Tem certeza de que deseja finalizar o simulado?'
          buttonCancelText='Voltar'
          buttonOkText='Finalizar simulado'
          isByTime={isByTime}
          onConfirm={() => finish()}
          isOpen={isOpen}
          onClose={() => onClose()}
          warns={
            hasPendences() ? (
              <Fragment>
                {!isByTime ? (
                  <Fragment>
                    <Typography>
                      <b>
                        Há{' '}
                        {
                          choices?.filter((it) => !it.hasAlternativeSelected)
                            ?.length
                        }{' '}
                        questões sem resposta.
                      </b>{' '}
                      Se você finalizar o simulado, essas respostas serão
                      consideradas erradas.
                    </Typography>
                  </Fragment>
                ) : (
                  <Fragment>
                    <Typography weight='bold'>
                      Há{' '}
                      {
                        choices?.filter((it) => !it.hasAlternativeSelected)
                          ?.length
                      }{' '}
                      perguntas sem resposta.
                    </Typography>
                    <Typography>
                      Se você finalizar o simulado, essas respostas serão
                      consideradas erradas.
                    </Typography>
                  </Fragment>
                )}
              </Fragment>
            ) : null
          }
          hasWarn={hasPendences()}
          loading={pendingEssay || pendingFinish}
          isTextSent={isTextSent}
        />
      </Container>
    </div>
  );
}
